import React from 'react';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { PanelBody, PanelButtonLink, PanelHeading, PanelLink, TableExpandButton } from '../../elements';
import { FmecaRiskLabel } from '../../utils/FmecaRiskLabel';
import { TotalRiskScoreDot } from '../../utils/TotalRiskScoreDot';
import { IElement } from '../Interfaces/IElement';
import { IFailureMode } from '../Interfaces/IFailureMode';
import { IManifestation } from '../Interfaces/IManifestation';
import { IUnit } from '../Interfaces/IUnit';
import { ConditionReportFMECAAnalysis } from './ConditionReportFMECAAnalysis';
import { ConditionReportFMECAMaintenance } from './ConditionReportFMECAMaintenance';
import { ConditionReportFMECAVerification } from './ConditionReportFMECAVerification';

interface IConditionReportFmecaProps extends ITranslateProps {
    surveyId: string;
    breakDownOpenState: { [key: string]: boolean };
    elements: IElement[];
    failureModes: IFailureMode[];
    isAssetDescriptionVisible: boolean;
    isLoadingElements: boolean;
    onExpandAll: () => void;
    onCollapseAll: () => void;
    onChangeBreakdownOpenState: (id: string) => void;
}

interface IConditionReportFmecaState {
    breakDownOpenStateFailureMode: { [key: string]: boolean };
}

class ConditionReportFmecaComponent extends React.Component<IConditionReportFmecaProps, IConditionReportFmecaState> {
    constructor(props: IConditionReportFmecaProps) {
        super(props);

        this.state = {
            breakDownOpenStateFailureMode: {},
        };
    }

    public override render() {
        return (
            <div className="panel" id="FMECAConditionAssessment">
                <PanelHeading text={`FMECA ${this.props.t('survey.conditionReport')}`} icon="fa fa-tasks">
                    <PanelLink
                        href={`/api/surveys/${this.props.surveyId}/fmecaExcel`}
                        text="Excel export"
                        isDownload={true}
                        icon="fa fa-download"
                        small={true}
                    />
                </PanelHeading>
                <PanelBody whiteBackground={true}>
                    <PanelButtonLink
                        onClick={() => this.props.onExpandAll()}
                        text={this.props.t('survey.expandAllComposition')}
                        icon="fa fa-plus-square-o"
                        xsmall={true}
                        rightAllign={true}
                    />
                    <PanelButtonLink
                        onClick={() => this.props.onCollapseAll()}
                        text={this.props.t('survey.collapseAllComposition')}
                        icon="fa fa-minus-square-o "
                        xsmall={true}
                        rightAllign={true}
                    />

                    {this.props.isLoadingElements && (
                        <table className="table">
                            <thead key="spinner">
                                <tr>
                                    <th className="text-center">
                                        <img src="/assets/img/spinner-amsterdam.svg" alt="loading" />
                                    </th>
                                </tr>
                            </thead>
                        </table>
                    )}
                    <div id="printview">
                        {this.props.elements.length > 0 && (
                            <table className="table">
                                <thead className="mt10">
                                    <tr className="dark">
                                        <th style={{ width: '50px' }}>{this.props.t('common.code')}</th>
                                        <th>{this.props.t('common.part')}</th>
                                        <th>{this.props.t('common.material')}</th>
                                        <th>{this.props.t('common.year')}</th>
                                        <th className="text-right" style={{ width: '40px' }}>
                                            {this.props.t('failureMode.analysis')}
                                        </th>
                                        <th className="text-right" style={{ width: '40px' }}>
                                            {this.props.t('failureMode.verification')}
                                        </th>
                                        <th className="text-right" style={{ width: '40px' }}>
                                            {this.props.t('failureMode.maintenance')}
                                        </th>
                                        <th>&nbsp; </th>
                                    </tr>
                                </thead>
                                {this.props.elements.map(
                                    (element) =>
                                        this.shouldShowElement(element) && (
                                            <React.Fragment key={element.id}>
                                                <thead className="object-table-element" key={element.id}>
                                                    <tr className="primary">
                                                        <th>{element.code}</th>
                                                        <th>{element.name}</th>
                                                        <th>&nbsp;</th>
                                                        <th>{element.constructionYear}</th>
                                                        <th className="text-center">
                                                            <TotalRiskScoreDot
                                                                value={this.getElementRiskScore(element, 'analysisRamsWeightedPriority')}
                                                            />
                                                        </th>
                                                        <th className="text-center">
                                                            <TotalRiskScoreDot
                                                                value={this.getElementRiskScore(element, 'verificationRamsWeightedPriority')}
                                                            />
                                                        </th>
                                                        <th className="text-center">
                                                            <TotalRiskScoreDot
                                                                value={this.getElementRiskScore(element, 'maintenanceRamsWeightedPriority')}
                                                            />
                                                        </th>
                                                        <th className="text-right">
                                                            {element.units && element.units.length > 0 && (
                                                                <TableExpandButton
                                                                    open={this.props.breakDownOpenState[element.id]}
                                                                    onClick={() => this.props.onChangeBreakdownOpenState(element.id)}
                                                                />
                                                            )}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody
                                                    className={this.props.breakDownOpenState[element.id] ? 'object-table-unit' : 'hidden'}
                                                    key={`${element.id}tbody`}
                                                >
                                                    {element.units.map(
                                                        (unit) =>
                                                            this.shouldShowUnit(unit) && (
                                                                <React.Fragment key={unit.id}>
                                                                    <tr key={unit.id} className="bg-light darker">
                                                                        <td>{unit.code}</td>
                                                                        <td>{unit.name}</td>
                                                                        <td>{unit.material}</td>
                                                                        <td>{unit.constructionYear}</td>
                                                                        <td className="text-center">
                                                                            <TotalRiskScoreDot
                                                                                value={this.getUnitRiskScore(unit, 'analysisRamsWeightedPriority')}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <TotalRiskScoreDot
                                                                                value={this.getUnitRiskScore(
                                                                                    unit,
                                                                                    'verificationRamsWeightedPriority',
                                                                                )}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <TotalRiskScoreDot
                                                                                value={this.getUnitRiskScore(unit, 'maintenanceRamsWeightedPriority')}
                                                                            />
                                                                        </td>
                                                                        <td className="text-right">
                                                                            {((unit.manifestations && unit.manifestations.length > 0) ||
                                                                                this.props.failureModes.filter(
                                                                                    (failureMode) =>
                                                                                        failureMode.unit.id === unit.id &&
                                                                                        !failureMode.manifestation.id,
                                                                                ).length > 0) && (
                                                                                <TableExpandButton
                                                                                    open={this.props.breakDownOpenState[unit.id]}
                                                                                    onClick={() => this.props.onChangeBreakdownOpenState(unit.id)}
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                    {this.props.failureModes
                                                                        .filter(
                                                                            (failureMode) =>
                                                                                failureMode.unit.id === unit.id && !failureMode.manifestation.id,
                                                                        )
                                                                        .map((failureMode) =>
                                                                            this.renderFailureModes(
                                                                                failureMode,
                                                                                this.props.breakDownOpenState[unit.id],
                                                                            ),
                                                                        )}
                                                                    {unit.manifestations.map((manifestation) => (
                                                                        <React.Fragment key={manifestation.id}>
                                                                            <tr
                                                                                key={manifestation.id}
                                                                                className={
                                                                                    this.props.breakDownOpenState[unit.id] ? 'bg-light' : 'hidden'
                                                                                }
                                                                            >
                                                                                <td style={{ whiteSpace: 'nowrap' }}>{manifestation.code}</td>
                                                                                <td>{manifestation.name}</td>
                                                                                <td>&nbsp;</td>
                                                                                <td>&nbsp;</td>
                                                                                <td className="text-center">
                                                                                    <TotalRiskScoreDot
                                                                                        value={this.getManifestationRiskScore(
                                                                                            manifestation,
                                                                                            'analysisRamsWeightedPriority',
                                                                                        )}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <TotalRiskScoreDot
                                                                                        value={this.getManifestationRiskScore(
                                                                                            manifestation,
                                                                                            'verificationRamsWeightedPriority',
                                                                                        )}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <TotalRiskScoreDot
                                                                                        value={this.getManifestationRiskScore(
                                                                                            manifestation,
                                                                                            'maintenanceRamsWeightedPriority',
                                                                                        )}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-right">
                                                                                    {this.props.failureModes.filter(
                                                                                        (failureMode) =>
                                                                                            failureMode.manifestation.id === manifestation.id,
                                                                                    ).length > 0 && (
                                                                                        <TableExpandButton
                                                                                            open={this.props.breakDownOpenState[manifestation.id]}
                                                                                            onClick={() =>
                                                                                                this.props.onChangeBreakdownOpenState(
                                                                                                    manifestation.id,
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                            {this.props.failureModes
                                                                                .filter(
                                                                                    (failureMode) =>
                                                                                        failureMode.manifestation.id === manifestation.id,
                                                                                )
                                                                                .map((failureMode) =>
                                                                                    this.renderFailureModes(
                                                                                        failureMode,
                                                                                        this.props.breakDownOpenState[manifestation.id],
                                                                                    ),
                                                                                )}
                                                                        </React.Fragment>
                                                                    ))}
                                                                </React.Fragment>
                                                            ),
                                                    )}
                                                </tbody>
                                            </React.Fragment>
                                        ),
                                )}
                            </table>
                        )}
                    </div>
                </PanelBody>
            </div>
        );
    }

    private renderFailureModes(failureMode: IFailureMode, isParentHidden?: boolean): JSX.Element {
        return (
            <React.Fragment key={failureMode.id}>
                <tr key={`${failureMode.id}base`} className={isParentHidden ? '' : 'hidden'}>
                    <td>&nbsp;</td>
                    <td colSpan={3}>
                        <strong>{failureMode.defaultFailureMode.name ?? failureMode.customName}</strong>
                    </td>
                    <td className="text-center">
                        <TotalRiskScoreDot value={failureMode.analysisRamsWeightedPriority} />
                    </td>
                    <td className="text-center">
                        <TotalRiskScoreDot value={failureMode.verificationRamsWeightedPriority} />
                    </td>
                    <td className="text-center">
                        <TotalRiskScoreDot value={failureMode.maintenanceRamsWeightedPriority} />
                    </td>
                    <td className="text-right">
                        {(failureMode.analysisRemarks ||
                            failureMode.metaData ||
                            failureMode.analysisRamsWeightedPriority ||
                            failureMode.verificationRamsWeightedPriority ||
                            failureMode.verificationRemarks ||
                            (failureMode.assets?.length ?? 0) > 0 ||
                            (failureMode.findings?.length ?? 0) > 0 ||
                            failureMode.maintenanceRamsWeightedPriority ||
                            failureMode.maintenanceRemarks ||
                            (failureMode.maintenanceMeasures?.length ?? 0) > 0) && (
                            <TableExpandButton
                                open={this.props.breakDownOpenState[failureMode.id]}
                                onClick={() => this.props.onChangeBreakdownOpenState(failureMode.id)}
                            />
                        )}
                    </td>
                </tr>

                {/* Analysis */}
                {(failureMode.analysisRemarks || failureMode.metaData || failureMode.analysisRamsWeightedPriority) && (
                    <tr key={`${failureMode.id}init`} className={isParentHidden && this.props.breakDownOpenState[failureMode.id] ? '' : 'hidden'}>
                        <td style={{ border: 'none' }}>&nbsp;</td>
                        <td colSpan={2} style={{ borderLeft: '1px solid #f4f4f4' }}>
                            <strong className="text-info ml10">{this.props.t('failureMode.analysis')}</strong>
                        </td>
                        <td className="text-right" colSpan={4}>
                            <FmecaRiskLabel
                                weightedValue={failureMode.analysisRamsWeightedPriority}
                                totalValue={failureMode.analysisRamsTotalPriority}
                            />
                        </td>
                        <td className="text-right">
                            <TableExpandButton
                                open={this.props.breakDownOpenState[`${failureMode.id}init`]}
                                onClick={() => this.props.onChangeBreakdownOpenState(`${failureMode.id}init`)}
                            />
                        </td>
                    </tr>
                )}
                <tr
                    key={`${failureMode.id}initdetail`}
                    className={isParentHidden && this.props.breakDownOpenState[`${failureMode.id}init`] ? '' : 'hidden'}
                >
                    <ConditionReportFMECAAnalysis failureMode={failureMode} />
                </tr>

                {/* Verification */}
                {(failureMode.verificationRamsWeightedPriority ||
                    failureMode.verificationRemarks ||
                    (failureMode.assets?.length ?? 0) > 0 ||
                    (failureMode.findings?.length ?? 0) > 0) && (
                    <tr
                        key={`${failureMode.id}verification`}
                        className={isParentHidden && this.props.breakDownOpenState[failureMode.id] ? '' : 'hidden'}
                    >
                        <td style={{ border: 'none' }}>&nbsp;</td>
                        <td colSpan={2} style={{ borderLeft: '1px solid #f4f4f4' }}>
                            <strong className="text-info ml10">{this.props.t('failureMode.verification')}</strong>
                        </td>
                        <td className="text-right" colSpan={4}>
                            <FmecaRiskLabel
                                weightedValue={failureMode.verificationRamsWeightedPriority}
                                totalValue={failureMode.verificationRamsTotalPriority}
                            />
                        </td>
                        <td className="text-right">
                            <TableExpandButton
                                open={this.props.breakDownOpenState[`${failureMode.id}verification`]}
                                onClick={() => this.props.onChangeBreakdownOpenState(`${failureMode.id}verification`)}
                            />
                        </td>
                    </tr>
                )}
                <tr
                    key={`${failureMode.id}verificationdetail`}
                    className={isParentHidden && this.props.breakDownOpenState[`${failureMode.id}verification`] ? '' : 'hidden'}
                >
                    <ConditionReportFMECAVerification
                        failureMode={failureMode}
                        isAssetDescriptionVisible={this.props.isAssetDescriptionVisible}
                        surveyId={this.props.surveyId}
                    />
                </tr>

                {/* Maintenance */}
                {(failureMode.maintenanceRamsWeightedPriority ||
                    failureMode.maintenanceRemarks ||
                    (failureMode.maintenanceMeasures?.length ?? 0) > 0) && (
                    <tr key={`${failureMode.id}target`} className={isParentHidden && this.props.breakDownOpenState[failureMode.id] ? '' : 'hidden'}>
                        <td style={{ border: 'none' }}>&nbsp;</td>
                        <td colSpan={2} style={{ borderLeft: '1px solid #f4f4f4' }}>
                            <strong className="text-info ml10">{this.props.t('failureMode.maintenance')}</strong>
                        </td>
                        <td className="text-right" colSpan={4}>
                            <FmecaRiskLabel
                                weightedValue={failureMode.maintenanceRamsWeightedPriority}
                                totalValue={failureMode.maintenanceRamsTotalPriority}
                            />
                        </td>
                        <td className="text-right">
                            <TableExpandButton
                                open={this.props.breakDownOpenState[`${failureMode.id}target`]}
                                onClick={() => this.props.onChangeBreakdownOpenState(`${failureMode.id}target`)}
                            />
                        </td>
                    </tr>
                )}
                <tr
                    key={`${failureMode.id}targetdetail`}
                    className={isParentHidden && this.props.breakDownOpenState[`${failureMode.id}target`] ? '' : 'hidden'}
                >
                    <ConditionReportFMECAMaintenance failureMode={failureMode} />
                </tr>
            </React.Fragment>
        );
    }

    private shouldShowElement(element: IElement) {
        if (!element.isArchived) {
            return true;
        }

        if (this.getElementRiskScore(element, 'analysisRamsWeightedPriority') !== '0') {
            return true;
        }

        if (this.getElementRiskScore(element, 'verificationRamsWeightedPriority') !== '0') {
            return true;
        }

        if (this.getElementRiskScore(element, 'maintenanceRamsWeightedPriority') !== '0') {
            return true;
        }
        for (const unit of element.units) {
            if (this.hasUnitInformation(unit)) {
                return true;
            }
        }

        return false;
    }

    private shouldShowUnit(unit: IUnit) {
        if (!unit.isArchived) {
            return true;
        }

        if (this.hasUnitInformation(unit)) {
            return true;
        }

        return false;
    }

    private hasUnitInformation(unit: IUnit) {
        if (this.getUnitRiskScore(unit, 'analysisRamsWeightedPriority') !== '0') {
            return true;
        }
        if (this.getUnitRiskScore(unit, 'verificationRamsWeightedPriority') !== '0') {
            return true;
        }
        if (this.getUnitRiskScore(unit, 'maintenanceRamsWeightedPriority') !== '0') {
            return true;
        }
        for (const manifestation of unit.manifestations) {
            if (this.getManifestationRiskScore(manifestation, 'analysisRamsWeightedPriority') !== '0') {
                return true;
            }
            if (this.getManifestationRiskScore(manifestation, 'verificationRamsWeightedPriority') !== '0') {
                return true;
            }
            if (this.getManifestationRiskScore(manifestation, 'maintenanceRamsWeightedPriority') !== '0') {
                return true;
            }
        }

        return false;
    }

    private getManifestationRiskScore(
        manifestation: IManifestation,
        scoreType: 'analysisRamsWeightedPriority' | 'verificationRamsWeightedPriority' | 'maintenanceRamsWeightedPriority',
    ): string {
        const filteredFailureModes = this.props.failureModes.filter((failureMode: IFailureMode) => {
            return failureMode.manifestation.id === manifestation.id;
        });
        return this.convertScore(Math.max(...filteredFailureModes.map((failureMode) => this.getInt(failureMode[scoreType]))));
    }

    private getUnitRiskScore(
        unit: IUnit,
        scoreType: 'analysisRamsWeightedPriority' | 'verificationRamsWeightedPriority' | 'maintenanceRamsWeightedPriority',
    ): string {
        const filteredFailureModes = this.props.failureModes.filter((failureMode: IFailureMode) => {
            return failureMode.unit.id === unit.id;
        });
        return this.convertScore(Math.max(...filteredFailureModes.map((failureMode) => this.getInt(failureMode[scoreType]))));
    }

    private getElementRiskScore(
        element: IElement,
        scoreType: 'analysisRamsWeightedPriority' | 'verificationRamsWeightedPriority' | 'maintenanceRamsWeightedPriority',
    ): string {
        const filteredFailureModes = this.props.failureModes.filter((failureMode: IFailureMode) => {
            return failureMode.elementId === element.id;
        });
        return this.convertScore(Math.max(...filteredFailureModes.map((failureMode) => this.getInt(failureMode[scoreType]))));
    }

    private convertScore(value: number): string {
        if (value && value !== 0 && isFinite(value)) {
            return value.toString();
        }
        return '0';
    }

    private getInt(value?: string | null): number {
        if (!value || value === '') {
            return 0;
        }
        return parseInt(value, 10);
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ConditionReportFMECA = translate()(ConditionReportFmecaComponent);
